import React, { useState } from 'react';
import { Search, Sparkles, BookOpen, Layers, Target, Languages, FileText, Upload, Trash2, Paperclip, AlertCircle, CheckCircle2 } from 'lucide-react';
import { ResearchOptions, ResearchDepth, ReportTemplate } from '../types';

interface ResearchFormProps {
  onSubmit: (query: string, options: ResearchOptions) => void;
  isSearching: boolean;
  templates: ReportTemplate[];
  activeTemplateId: string;
  onChangeActiveTemplateId: (id: string) => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const TEMPLATES = [
  {
    title: 'ШІ-Агенти & Ринок 2026',
    query: 'Аналіз ринку штучного інтелекту та автономних ШІ-агентів у 2026 році: тренди, обсяги та ключові гравці',
    options: { depth: 'max', focus: 'tech', format: 'comprehensive', language: 'uk' } as ResearchOptions
  },
  {
    title: 'Зелена Енергетика 2030',
    query: 'Порівняльний економічний аналіз систем зберігання енергії: літій-іонні батареї, водень та гравітаційні накопичувачі до 2030 року',
    options: { depth: 'max', focus: 'science', format: 'comparison', language: 'uk' } as ResearchOptions
  },
  {
    title: 'Криза Напівпровідників',
    query: 'Аналіз глобального ланцюжка поставок напівпровідників та літографічного обладнання ASML, вплив геополітики на чипи',
    options: { depth: 'standard', focus: 'finance', format: 'swot', language: 'uk' } as ResearchOptions
  },
  {
    title: 'Квантові Обчислення',
    query: 'Комерціалізація квантових обчислень та активні бізнес-додатки в криптографії та логістиці на найближчі роки',
    options: { depth: 'standard', focus: 'tech', format: 'executive', language: 'uk' } as ResearchOptions
  }
];

export default function ResearchForm({ 
  onSubmit, 
  isSearching,
  templates,
  activeTemplateId,
  onChangeActiveTemplateId,
  useEnterpriseContext,
  enterpriseName
}: ResearchFormProps) {
  const [query, setQuery] = useState('');
  const [depth, setDepth] = useState<ResearchDepth>('standard');
  const [focus, setFocus] = useState<ResearchOptions['focus']>('general');
  const language = 'uk';

  // File upload states
  const [uploadedDocs, setUploadedDocs] = useState<{ name: string; size: number; text: string }[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/extract-text', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || (language === 'uk' ? 'Помилка обробки файлу.' : 'Error parsing file.'));
      }

      const docData = await response.json();
      setUploadedDocs(prev => [...prev, docData]);
    } catch (err: any) {
      console.error('File extraction failed:', err);
      setUploadError(err.message || err);
    } finally {
      setIsUploading(false);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleFileUpload(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (['pdf', 'docx', 'txt'].includes(ext || '')) {
        handleFileUpload(file);
      } else {
        setUploadError(language === 'uk' 
          ? 'Невідповідний формат. Дозволені тільки .pdf, .docx, .txt' 
          : 'Invalid format. Only .pdf, .docx, .txt are allowed.');
      }
    }
  };

  const removeDoc = (indexToRemove: number) => {
    setUploadedDocs(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isSearching) return;
    onSubmit(query.trim(), { 
      depth, 
      focus, 
      format: 'comprehensive', 
      language,
      templateId: activeTemplateId,
      customTemplate: templates.find(t => t.id === activeTemplateId),
      uploadedDocuments: uploadedDocs
    } as any);
  };

  const applyTemplate = (tmpl: typeof TEMPLATES[0]) => {
    if (isSearching) return;
    setQuery(tmpl.query);
    setDepth(tmpl.options.depth);
    setFocus(tmpl.options.focus);
  };

  return (
    <div id="research-form" className="w-full bg-white border border-slate-200 rounded-lg p-5 sm:p-6 relative overflow-hidden shadow-xs">
      {/* Background radial highlight */}
      <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-red-500/[0.03] rounded-full blur-3xl pointer-events-none" />

      <h2 className="text-sm font-bold font-mono text-slate-800 flex items-center gap-2 mb-1.5 uppercase">
        <Sparkles className="w-4 h-4 text-red-500 animate-pulse" />
        {language === 'uk' ? 'КОНСОЛЬ ДОСЛІДЖЕННЯ MAX' : 'RESEARCH_MAX_COORDINATOR'}
      </h2>
      <p className="text-[11px] text-slate-500 mb-6 max-w-2xl font-mono leading-relaxed">
        {language === 'uk' 
          ? 'Введіть будь-який науково-технічний або економічний запит. Нейромережа сформує гіпотези, виконає серію пошуків у реальному часі, вилучить факти та синтезує звіт із графіками та джерелами.'
          : 'Enter any scientific, technical, or financial topic. The model will design hypotheses, search the live web, compile statistical data, and generate structured reports with charts.'}
      </p>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 mb-5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">Активовано контекст підприємства: </span>
              <span className="font-mono">{enterpriseName || "База знань компанії"}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            База знань
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 relative">
        {/* Main query bar */}
        <div className="relative">
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 font-mono">
            {language === 'uk' ? 'RESEARCH_QUERY:' : 'RESEARCH_QUERY:'}
          </label>
          <div className="relative">
            <input
              type="text"
              required
              disabled={isSearching}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={language === 'uk' ? "Наприклад: Розвиток термоядерного синтезу в другій половині 2020-х років..." : "E.g., Breakthroughs in nuclear fusion commercialization..."}
              className="w-full text-xs bg-slate-50 text-slate-900 placeholder-slate-400 pl-10 pr-4 py-3 rounded border border-slate-200 focus:outline-none focus:border-red-500 transition-all disabled:opacity-60 font-mono"
            />
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          </div>
        </div>

        {/* Document Upload Area */}
        <div className="space-y-2">
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest font-mono">
            {language === 'uk' ? 'СУПРОВІДНІ ДОКУМЕНТИ (PDF, DOCX, TXT):' : 'SUPPORTING DOCUMENTS (PDF, DOCX, TXT):'}
          </label>
          
          <div 
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            className={`border border-dashed rounded-lg p-4 transition-all flex flex-col items-center justify-center cursor-pointer relative ${
              dragActive 
                ? 'border-red-500 bg-red-50' 
                : 'border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-50'
            } ${isSearching ? 'pointer-events-none opacity-50' : ''}`}
            onClick={() => {
              if (!isSearching) {
                document.getElementById('file-upload-input')?.click();
              }
            }}
          >
            <input 
              id="file-upload-input"
              type="file"
              className="hidden"
              accept=".pdf,.docx,.txt"
              disabled={isSearching}
              onChange={onFileChange}
            />

            <Upload className={`w-5 h-5 mb-2 transition-colors ${dragActive ? 'text-red-500' : 'text-slate-400'}`} />
            
            <p className="text-[10px] font-bold text-slate-800 uppercase font-mono tracking-wide text-center">
              {language === 'uk' 
                ? 'Перетягніть файли сюди або натисніть для вибору' 
                : 'Drag & drop files here or click to browse'}
            </p>
            <p className="text-[8px] text-slate-500 font-mono mt-1 text-center">
              PDF, DOCX, TXT — MAX 15MB
            </p>
          </div>

          {/* Upload progress & status */}
          {isUploading && (
            <div className="flex items-center gap-2 text-[10px] font-mono text-red-600 bg-red-50 border border-red-200 rounded px-3 py-1.5 animate-pulse">
              <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              <span>{language === 'uk' ? 'ЗЧИТУВАННЯ ТА СТРУКТУРУВАННЯ ТЕКСТУ ДОКУМЕНТА...' : 'READING AND STRUCTURING DOCUMENT TEXT...'}</span>
            </div>
          )}

          {uploadError && (
            <div className="flex items-center gap-2 text-[10px] font-mono text-red-600 bg-red-50 border border-red-200 rounded px-3 py-1.5">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{uploadError}</span>
            </div>
          )}

          {/* Uploaded files list */}
          {uploadedDocs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {uploadedDocs.map((doc, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between bg-slate-50 border border-slate-200 hover:border-slate-300 rounded p-2.5 transition-all"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    <Paperclip className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold font-mono text-slate-800 truncate uppercase">{doc.name}</p>
                      <p className="text-[8px] font-mono text-slate-500 mt-0.5">{formatSize(doc.size)}</p>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    disabled={isSearching}
                    onClick={(e) => {
                       e.stopPropagation();
                       removeDoc(idx);
                    }}
                    className="p-1 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded transition-all cursor-pointer disabled:opacity-40"
                    title={language === 'uk' ? 'Видалити документ' : 'Remove document'}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Options grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* 1. Depth Selector */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1 font-mono">
              <Layers className="w-3.5 h-3.5 text-slate-400" />
              ГЛИБИНА_ПОШУКУ
            </label>
            <div className="grid grid-cols-2 gap-1 bg-slate-100 p-1 rounded border border-slate-200">
              <button
                type="button"
                disabled={isSearching}
                onClick={() => setDepth('standard')}
                className={`py-1 px-2 text-[10px] font-bold uppercase rounded font-mono transition-all cursor-pointer ${
                  depth === 'standard'
                    ? 'bg-white text-slate-800 shadow-xs'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                СТАНДАРТНА
              </button>
              <button
                type="button"
                disabled={isSearching}
                onClick={() => setDepth('max')}
                className={`py-1 px-2 text-[10px] font-bold uppercase rounded font-mono transition-all flex items-center justify-center gap-1 cursor-pointer ${
                  depth === 'max'
                    ? 'bg-red-500/10 text-red-600 border border-red-500/20'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                МАКСИМАЛЬНА
                <span className="bg-red-200 text-red-700 text-[8px] px-1 rounded">PRO</span>
              </button>
            </div>
          </div>

          {/* 2. Focus Sector */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1 font-mono">
              <Target className="w-3.5 h-3.5 text-slate-400" />
              НАПРЯМОК_ДОСЛІДЖЕННЯ
            </label>
            <select
              disabled={isSearching}
              value={focus}
              onChange={(e) => setFocus(e.target.value as any)}
              className="w-full text-[10px] font-bold uppercase bg-white text-slate-800 py-2 px-2.5 rounded border border-slate-200 focus:outline-none focus:border-red-500 transition-all font-mono"
            >
              <option value="general">ЗАГАЛЬНИЙ_АНАЛІЗ</option>
              <option value="tech">ТЕХНОЛОГІЧНИЙ_СЕКТОР</option>
              <option value="finance">ФІНАНСИ_ТА_ЕКОНОМІКА</option>
              <option value="science">НАУКА_ТА_ЕНЕРГЕТИКА</option>
              <option value="medical">БІОТЕХ_ТА_МЕДИЦИНА</option>
            </select>
          </div>

          {/* 3. Report Template Selector */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1 font-mono">
              <FileText className="w-3.5 h-3.5 text-slate-400" />
              ШАБЛОН ЗВІТУ
            </label>
            <select
              disabled={isSearching}
              value={activeTemplateId}
              onChange={(e) => onChangeActiveTemplateId(e.target.value)}
              className="w-full text-[10px] font-bold uppercase bg-white text-slate-800 py-2 px-2.5 rounded border border-slate-200 focus:outline-none focus:border-red-500 transition-all font-mono"
            >
              {templates.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.isCustom ? '★' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Submit button */}
        <div className="pt-2 flex justify-end">
          <button
            type="submit"
            disabled={!query.trim() || isSearching}
            className="w-full sm:w-auto px-6 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 disabled:bg-slate-100 disabled:text-slate-400 text-white font-bold rounded text-[11px] uppercase tracking-wider transition-all flex items-center justify-center gap-2 font-mono cursor-pointer shadow-xs"
          >
            {isSearching ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>COGNITION_ACTIVE...</span>
              </>
            ) : (
              <>
                <Search className="w-3.5 h-3.5" />
                <span>LAUNCH_DEEP_PROBE</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Quick templates panel */}
      <div className="mt-6 pt-5 border-t border-slate-200">
        <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-2 font-mono">
          <BookOpen className="w-3.5 h-3.5 text-slate-400" />
          {language === 'uk' ? 'КОГНІТИВНІ ШАБЛОНИ (PROBE_TEMPLATES)' : 'PROBE_TEMPLATES'}
        </h4>
        <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
          {TEMPLATES.map((tmpl, idx) => (
            <button
              key={idx}
              type="button"
              disabled={isSearching}
              onClick={() => applyTemplate(tmpl)}
              className="text-left p-3 rounded border border-slate-200 bg-slate-50/50 hover:bg-slate-50 hover:border-red-500/30 transition-all duration-150 cursor-pointer disabled:opacity-50 min-w-[260px] sm:min-w-0 flex-1 shrink-0"
            >
              <p className="text-[10px] font-bold text-slate-800 uppercase font-mono tracking-tight mb-1 flex items-center justify-between">
                <span>{tmpl.title}</span>
                <span className="text-[8px] px-1 bg-red-50 border border-red-200 text-red-600 rounded font-mono">
                  {tmpl.options.focus.toUpperCase()}
                </span>
              </p>
              <p className="text-[10px] text-slate-500 font-mono line-clamp-2 leading-relaxed">
                {tmpl.query}
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
